import { Component, OnInit, Input, Optional, Self, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import * as uuid from 'uuid';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, filter, mergeMap, map, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { ResizeProfilePictureModalComponent } from '../resize-profile-picture-modal/resize-profile-picture-modal.component';
import { UploadModalComponent } from '../upload-modal/upload-modal.component';

@Component({
    selector: 'app-form-profile-picture',
    templateUrl: './profile-picture.component.html',
    styleUrls: ['./profile-picture.component.scss'],
})
export class ProfilePictureComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private destroy$ = new Subject();

    public onChange: (x?: any) => void;

    public onTouched: (x?: any) => void;

    public id: string;

    public _value: any = '';

    public preview: string;

    public t: any;

    loading = false;

    @Input() autoUpload = false;

    @Input() disabled: boolean;

    @Input() label: string;

    @Input() disableMessages: boolean = false;

    @Input() imgPlaceholder: boolean = true;

    @Output('change') changeOutput = new EventEmitter<any>();

    constructor(
        public dialog: MatDialog,
        @Self() @Optional() private ngControl: NgControl,
        private translateService: TranslateService,
        private userService: UserService,
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.translateService
            .get('common.profilePicture')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));
    }

    get invalid(): boolean {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid(): boolean {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine(): boolean {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty(): boolean {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched(): boolean {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    public ngOnInit(): void {
        this.id = uuid.v4();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public valueChange(v): void {
        this.value = v;
    }

    public writeValue(value: any): void {
        this._value = value;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }

    openDialog(): void {
        this.dialog
            .open(UploadModalComponent, {
                data: {
                    title: this.t.modalTitle,
                    restrictionMessage: this.t.restrictions,
                    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
                    maxFileSize: 20000000,
                    autoComplete: true,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                filter((files) => !!files && files.length > 0),
                map((files) => files[0]),
                mergeMap((file) =>
                    this.dialog
                        .open(ResizeProfilePictureModalComponent, {
                            data: { image: file },
                        })
                        .afterClosed(),
                ),
                tap(async (file) => {
                    if (file) {
                        this.loading = true;
                        return !this.autoUpload && (this.preview = await this.toBase64(file));
                    }
                }),
                tap((file) => this.valueChange(file)),
            )
            .subscribe();
    }

    async toBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (): void => resolve(reader.result as string);
            reader.onerror = (error): void => reject(error);
        });
    }

    imageLoaded(): void {
        this.loading = false;
    }
}
